// AI Service - Main entry point
// Routes requests to appropriate provider

import { callClaude, stopClaude } from './providers/claude'
import { callOpenAI, stopOpenAI } from './providers/openai'
import { callKimi, stopKimi } from './providers/kimi'
import { callGemini, stopGemini } from './providers/gemini'
import { callXelsed, stopXelsed } from './providers/xelsed'
import { SYSTEM_PROMPT, buildContext, getUserApiKey, getCorrectModel, cleanMessages, getClaudeOptions } from './utils'

// Export stop functions
export function stopAI() {
  stopClaude()
  stopOpenAI()
  stopKimi()
  stopGemini()
  stopXelsed()
}

// Main AI call function - routes to appropriate provider
export async function callAI(
  messages, 
  files, 
  consoleMessages, 
  p5Version, 
  provider = 'openai', 
  model = 'gpt-5-mini',
  reasoningMode = 'low',
  onThinking = null,
  thinkingLevel = 'high', // lowercase for Gemini API
  onDelta = null  // Real-time content streaming callback
) {
  try {
    // Auto-correct model based on provider
    model = getCorrectModel(provider, model)
    
    // Build context with all project information
    const fileContext = buildContext(files, consoleMessages, p5Version)
    
    // Clean messages (only role and content)
    const cleanedMessages = cleanMessages(messages)
    
    // Add system prompt with context
    const contextMessages = [
      {
        role: 'system',
        content: SYSTEM_PROMPT + fileContext
      },
      ...cleanedMessages
    ]

    // Get user's API key if they provided one
    const userApiKey = getUserApiKey(provider)

    // Claude-specific behavior flags (from settings)
    const claudeOptions = provider === 'anthropic' ? getClaudeOptions() : null
    
    // Use the reasoning mode passed as parameter (already set by UI)
    const openaiReasoningMode = reasoningMode
    
    if (!userApiKey && !isServerKeyAvailable(provider)) {
      const providerName = getProviderName(provider)
      throw new Error(`Please configure your ${providerName} API key in AI Settings (click the gear icon)`)
    }

    // Route to appropriate provider
    console.log(`🎯 Routing to ${provider} provider...`)
    
    switch (provider) {
      case 'anthropic':
        return await callClaude(contextMessages, userApiKey, model, claudeOptions || {})
      
      case 'openai':
        return await callOpenAI(contextMessages, userApiKey, model, openaiReasoningMode, onThinking)
      
      case 'kimi':
        return await callKimi(contextMessages, userApiKey, model)
      
      case 'gemini':
        return await callGemini(contextMessages, userApiKey, model, thinkingLevel, onThinking)
      
      case 'xelsed':
        return await callXelsed(contextMessages, userApiKey, model, onDelta, onThinking)
      
      default:
        throw new Error(`Unsupported AI provider: ${provider}`)
    }

  } catch (error) {
    // Handle abort errors gracefully (user cancelled)
    if (error.name === 'AbortError') {
      console.log('AI request cancelled by user')
      throw new Error('Request cancelled')
    }
    
    console.error('AI Service Error:', error)
    
    // Provide more helpful error messages
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      throw new Error('Network error. Check your internet connection and try again.')
    }
    
    throw error
  }
}

// Helper: Check if server keys are available (user has "Use Server Keys" checked)
function isServerKeyAvailable(provider) {
  try {
    const savedSettings = localStorage.getItem('ai_settings')
    const settings = savedSettings ? JSON.parse(savedSettings) : { useServerKeys: true }
    return settings.useServerKeys
  } catch (error) {
    // Safari tracking prevention - default to using server keys
    return true
  }
}

// Helper: Get friendly provider name
function getProviderName(provider) {
  switch (provider) {
    case 'openai': return 'OpenAI'
    case 'anthropic': return 'Anthropic'
    case 'kimi': return 'Kimi AI'
    case 'gemini': return 'Google Gemini'
    default: return provider
  }
}

